// ── Browser → relay HTTP helper for Polymarket US ─────────────────────────
//
// Thin typed wrappers over `/api/polymarket-us/*` (relay/src/routes/
// polymarket-us.ts). The US venue is per-trader keyed: every call runs on the
// caller's own Polymarket US API key, so there is no org-key mode and no
// personal-key browser path — the browser never holds the secret after the
// add form posts it.
//
// Two codes matter for gating UI, both surfaced as PolymarketUsRelayError:
//   POLYMARKET_US_NOT_CONFIGURED (503) — the lane is off on this deployment;
//     surfaces should hide themselves, not explain.
//   NO_POLY_US_CRED (404) — the org has no key on file; surfaces point the
//     trader at a manager.

import { apiFetch } from '@/stores/authStore';

export type PolymarketUsRelayCode =
  | 'POLYMARKET_US_NOT_CONFIGURED'
  | 'NO_POLY_US_CRED'
  | 'POLYMARKET_US_RATE_LIMITED'
  | 'POLYMARKET_US_STOPGAP_REJECTED'
  | 'POLYMARKET_US_AUTH'
  | 'POLYMARKET_US_API_ERROR'
  | 'POLYMARKET_US_NETWORK'
  | string;

export class PolymarketUsRelayError extends Error {
  readonly status: number;
  readonly code: PolymarketUsRelayCode | null;
  readonly retryAfterMs: number | null;
  constructor(args: { status: number; code?: string | null; message: string; retryAfterMs?: number | null }) {
    super(args.message);
    this.name = 'PolymarketUsRelayError';
    this.status = args.status;
    this.code = args.code ?? null;
    this.retryAfterMs = args.retryAfterMs ?? null;
  }
  get laneOff(): boolean {
    return this.code === 'POLYMARKET_US_NOT_CONFIGURED';
  }
  get noCredential(): boolean {
    return this.code === 'NO_POLY_US_CRED';
  }
}

async function relay<T>(path: string, init: RequestInit = {}): Promise<T> {
  const res = await apiFetch(`/api/polymarket-us${path}`, init);
  let body: unknown = null;
  try {
    body = await res.json();
  } catch {
    body = null;
  }
  if (!res.ok) {
    const b = (body ?? {}) as { error?: string; code?: string; retryAfterMs?: number | null };
    throw new PolymarketUsRelayError({
      status: res.status,
      code: b.code ?? null,
      message: b.error ?? `Polymarket US relay request failed (${res.status})`,
      retryAfterMs: b.retryAfterMs ?? null,
    });
  }
  return body as T;
}

function json(body: unknown): RequestInit {
  return { method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify(body) };
}

// ── Status (lane probe + Settings notice) ─────────────────────────────────
// Keys are org-pooled: managers add them under Org Settings, traders only
// see whether the org has one. The lane store probes this once per session.

export interface PolymarketUsStatus {
  lane: true;
  orgKey: { id: string; label: string; keyId: string } | null;
  credentialContext: PolymarketUsCredentialContext;
}

export async function fetchPolymarketUsStatus(): Promise<PolymarketUsStatus> {
  return relay<PolymarketUsStatus>('/status');
}

// ── Account ───────────────────────────────────────────────────────────────

export interface PolymarketUsBalance {
  currency?: string;
  currentBalance?: string | number;
  buyingPower?: string | number;
  assetNotional?: string | number;
  assetAvailable?: string | number;
  openOrders?: string | number;
  marginRequirement?: string | number;
}

export async function fetchPolymarketUsBalances(): Promise<PolymarketUsBalance[]> {
  const r = await relay<{ balances: PolymarketUsBalance[] | { balances?: PolymarketUsBalance[] } }>('/balances');
  const b = r.balances;
  if (Array.isArray(b)) return b;
  return b?.balances ?? [];
}

/** The USD buying power as a number, or null when the venue shape is unknown. */
export function buyingPowerUsd(balances: PolymarketUsBalance[]): number | null {
  const usd = balances.find((b) => !b.currency || b.currency === 'USD') ?? balances[0];
  if (!usd || usd.buyingPower == null) return null;
  const n = Number(usd.buyingPower);
  return Number.isFinite(n) ? n : null;
}

// ── Orders ────────────────────────────────────────────────────────────────

export interface PlacePolymarketUsOrderArgs {
  marketSlug: string;
  side: 'yes' | 'no';
  action: 'buy' | 'sell';
  /** Price of the side being traded, in cents (NO orders pass the NO price). */
  priceCents: number;
  contracts: number;
  tif?: 'GTC' | 'GTD' | 'IOC' | 'FOK' | 'DAY';
  goodTillTime?: string;
  postOnly?: boolean;
  /** The market's orderPriceMinTickSize (0.01, 0.005, …) from the feed. */
  tickSize?: number | string | null;
  /** Defaults true (the human ticket). Runners pass false. */
  manual?: boolean;
}

export interface PolymarketUsOrder {
  id?: string;
  orderId?: string;
  marketSlug?: string;
  state?: string;
  intent?: string;
  price?: { value?: string };
  quantity?: string | number;
  quantityFilled?: string | number;
  [k: string]: unknown;
}

export async function placePolymarketUsOrder(args: PlacePolymarketUsOrderArgs): Promise<PolymarketUsOrder> {
  const r = await relay<{ result: PolymarketUsOrder }>('/orders', json(args));
  return r.result;
}

export async function listPolymarketUsOpenOrders(): Promise<PolymarketUsOrder[]> {
  const r = await relay<{ orders: PolymarketUsOrder[] }>('/orders');
  return r.orders ?? [];
}

/** The venue's cancel needs the market slug; pass it when known (the relay
 *  can recover it from the audit ledger or open orders otherwise). */
export async function cancelPolymarketUsOrder(orderId: string, marketSlug?: string): Promise<void> {
  const q = marketSlug ? `?marketSlug=${encodeURIComponent(marketSlug)}` : '';
  await relay(`/orders/${encodeURIComponent(orderId)}${q}`, { method: 'DELETE' });
}

export async function cancelAllPolymarketUsOrders(marketSlug?: string): Promise<string[]> {
  const q = marketSlug ? `?marketSlug=${encodeURIComponent(marketSlug)}` : '';
  const r = await relay<{ result: { canceledOrderIds?: string[] } }>(`/orders${q}`, { method: 'DELETE' });
  return r.result?.canceledOrderIds ?? [];
}

// ── Portfolio + ledger ────────────────────────────────────────────────────

export interface PolymarketUsFill {
  ledger_id: string;
  fill_id: string | null;
  order_id: string | null;
  market_slug: string;
  market_label: string | null;
  market_sport: string | null;
  side: 'yes' | 'no';
  action: 'buy' | 'sell';
  count: number;
  price_cents: number;
  is_taker: boolean;
  fees_cents: number | null;
  executed_at: string;
}

export type PolymarketUsCredentialContext =
  | 'NOT_IN_ORG'
  | 'PERSONAL_KEY_MODE'
  | 'NO_POLY_US_CRED'
  | 'CONTEXT_CHECK_FAILED'
  | null;

export async function fetchPolymarketUsFills(limit?: number): Promise<{
  fills: PolymarketUsFill[];
  credentialContext: PolymarketUsCredentialContext;
}> {
  const q = limit ? `?limit=${limit}` : '';
  return relay(`/fills${q}`);
}

export async function fetchPolymarketUsPositions(cursor?: string): Promise<Record<string, unknown>> {
  const q = cursor ? `?cursor=${encodeURIComponent(cursor)}` : '';
  return relay(`/positions${q}`);
}

// ── Markets (public gateway via the relay, on demand — never on a timer) ──

export interface PolymarketUsMarketSide {
  id?: string | number;
  description?: string | null;
  long?: boolean;
  price?: string | number | null;
  teamId?: string | number | null;
  team?: { id?: number; name?: string; abbreviation?: string; league?: string; alias?: string } | null;
  [k: string]: unknown;
}

export interface PolymarketUsMarket {
  id: string;
  slug: string | null;
  question?: string | null;
  title?: string | null;
  titleShort?: string | null;
  subtitle?: string | null;
  category?: string | null;
  active?: boolean | null;
  closed?: boolean | null;
  startDate?: string | null;
  endDate?: string | null;
  gameStartTime?: string | null;
  orderPriceMinTickSize?: number | string | null;
  minimumTradeQty?: number | string | null;
  feeCoefficient?: number | string | null;
  sportsMarketType?: string | null;
  marketSides?: PolymarketUsMarketSide[];
  bestBidQuote?: { value?: string } | null;
  bestAskQuote?: { value?: string } | null;
  volume?: number | string | null;
  volume24hr?: number | string | null;
  [k: string]: unknown;
}

export interface PolymarketUsBookEntry {
  px: { value: string; currency?: string };
  qty: string;
}

export interface PolymarketUsBook {
  marketSlug?: string;
  bids: PolymarketUsBookEntry[];
  offers: PolymarketUsBookEntry[];
  state?: string;
  stats?: { lastTradePx?: { value?: string }; currentPx?: { value?: string }; sharesTraded?: string; openInterest?: string; [k: string]: unknown };
  transactTime?: string;
}

export async function listPolymarketUsMarkets(args: {
  active?: boolean;
  closed?: boolean;
  limit?: number;
  offset?: number;
  sportsMarketTypes?: string[];
  categories?: string[];
} = {}): Promise<PolymarketUsMarket[]> {
  const qs = new URLSearchParams();
  if (args.active != null) qs.set('active', String(args.active));
  if (args.closed != null) qs.set('closed', String(args.closed));
  if (args.limit != null) qs.set('limit', String(args.limit));
  if (args.offset != null) qs.set('offset', String(args.offset));
  if (args.sportsMarketTypes?.length) qs.set('sportsMarketTypes', args.sportsMarketTypes.join(','));
  if (args.categories?.length) qs.set('categories', args.categories.join(','));
  const q = qs.toString();
  const r = await relay<{ markets?: PolymarketUsMarket[] }>(`/markets${q ? `?${q}` : ''}`);
  return r.markets ?? [];
}

export async function fetchPolymarketUsMarket(slug: string): Promise<PolymarketUsMarket> {
  const r = await relay<{ market: PolymarketUsMarket }>(`/markets/${encodeURIComponent(slug)}`);
  return r.market;
}

export async function fetchPolymarketUsBook(slug: string): Promise<PolymarketUsBook> {
  const r = await relay<{ book: PolymarketUsBook }>(`/markets/${encodeURIComponent(slug)}/book`);
  return r.book;
}

// ── F4: typed read wrappers that mirror the intl /api/polymarket/* helpers ──
//
// The hooks in src/hooks/usePolymarketUsRelay*.ts are mechanical twins of the
// intl ones, so these wrappers answer in the same `{ ok, ... } | { ok: false,
// status, error, code? }` union. Two rules carried over from the intl helpers:
// a coded refusal is "venue absent / not configured", never an error banner
// (the lane-off 503 folds into that class), and a persisted-ledger read may
// carry an advisory `credentialContext` beside a perfectly good payload.

export type PolyUsRelayUnavailableCode =
  | 'NOT_IN_ORG'
  | 'PERSONAL_KEY_MODE'
  | 'NO_POLY_US_CRED'
  | 'POLYMARKET_US_NOT_CONFIGURED';

export type PolyUsReadCredentialContext =
  | Exclude<PolyUsRelayUnavailableCode, 'POLYMARKET_US_NOT_CONFIGURED'>
  | 'CONTEXT_CHECK_FAILED'
  | null;

export function parsePolyUsUnavailableCode(code: unknown): PolyUsRelayUnavailableCode | undefined {
  return code === 'NOT_IN_ORG' ||
    code === 'PERSONAL_KEY_MODE' ||
    code === 'NO_POLY_US_CRED' ||
    code === 'POLYMARKET_US_NOT_CONFIGURED'
    ? code
    : undefined;
}

export function parsePolyUsReadCredentialContext(value: unknown): PolyUsReadCredentialContext {
  if (value === null) return null;
  if (
    value === 'NOT_IN_ORG' ||
    value === 'PERSONAL_KEY_MODE' ||
    value === 'NO_POLY_US_CRED' ||
    value === 'CONTEXT_CHECK_FAILED'
  ) {
    return value;
  }
  // An older relay (or a shape we do not know) must not read as healthy.
  return 'CONTEXT_CHECK_FAILED';
}

type RelayFailure = { ok: false; status: number; error: string; code?: PolyUsRelayUnavailableCode };

async function readRelay(path: string, init: RequestInit = {}): Promise<
  { ok: true; status: number; body: Record<string, unknown> } | RelayFailure
> {
  let res: Response;
  try {
    res = await apiFetch(`/api/polymarket-us${path}`, init);
  } catch (err) {
    return { ok: false, status: 0, error: err instanceof Error ? err.message : 'Network error' };
  }
  let body: Record<string, unknown> = {};
  try {
    const parsed: unknown = await res.json();
    if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) body = parsed as Record<string, unknown>;
  } catch {
    /* non-JSON body */
  }
  if (res.ok) return { ok: true, status: res.status, body };
  return {
    ok: false,
    status: res.status,
    error: typeof body.error === 'string' ? body.error : `Polymarket US relay failed (${res.status})`,
    code: parsePolyUsUnavailableCode(body.code),
  };
}

function isRowObjectArray(rows: unknown): rows is Record<string, unknown>[] {
  return Array.isArray(rows) && rows.every((row) => !!row && typeof row === 'object' && !Array.isArray(row));
}

function num(v: unknown): number | null {
  if (typeof v === 'number') return Number.isFinite(v) ? v : null;
  if (typeof v === 'string' && v.trim() !== '') {
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
  }
  return null;
}

// ── Open orders ───────────────────────────────────────────────────────────

/** The normalized open order every FE surface reads. The relay (R4) sends
 *  these keys; older relays send the venue's own order object, which
 *  `normalizePolyUsOpenOrder` folds into the same shape so the hooks never
 *  branch on relay vintage. */
export interface PolyUsRelayOpenOrder {
  id: string;
  marketSlug: string;
  side: 'yes' | 'no';
  action: 'buy' | 'sell';
  /** Side-priced cents (a NO order carries the NO price). */
  priceCents: number;
  /** The venue's long-side price, cents (what it quotes on the wire). */
  priceCentsLong: number;
  quantity: number;
  quantityFilled: number;
  remaining: number;
  tif: 'GTC' | 'GTD' | 'IOC' | 'FOK' | 'DAY' | null;
  goodTillTime: string | null;
  postOnly: boolean | null;
  /** Short venue state: NEW, PARTIALLY_FILLED, PENDING_NEW, … */
  state: string | null;
  createdAt: string | null;
  marketLabel: string | null;
  marketSport: string | null;
}

const TIF_SHORT: Record<string, PolyUsRelayOpenOrder['tif']> = {
  TIME_IN_FORCE_GOOD_TILL_CANCEL: 'GTC',
  TIME_IN_FORCE_GOOD_TILL_DATE: 'GTD',
  TIME_IN_FORCE_IMMEDIATE_OR_CANCEL: 'IOC',
  TIME_IN_FORCE_FILL_OR_KILL: 'FOK',
  TIME_IN_FORCE_DAY: 'DAY',
  GTC: 'GTC',
  GTD: 'GTD',
  IOC: 'IOC',
  FOK: 'FOK',
  DAY: 'DAY',
};

function sideActionFromIntent(intent: unknown): { side: 'yes' | 'no'; action: 'buy' | 'sell' } | null {
  switch (intent) {
    case 'ORDER_INTENT_BUY_LONG':
      return { side: 'yes', action: 'buy' };
    case 'ORDER_INTENT_SELL_LONG':
      return { side: 'yes', action: 'sell' };
    case 'ORDER_INTENT_BUY_SHORT':
      return { side: 'no', action: 'buy' };
    case 'ORDER_INTENT_SELL_SHORT':
      return { side: 'no', action: 'sell' };
    default:
      return null;
  }
}

/** Pure. Accepts the relay's normalized row OR the venue's raw order object
 *  (`{id, marketSlug, intent, price:{value}, quantity, cumQuantity,
 *  leavesQuantity, tif, goodTillTime, state, marketMetadata}`); null when
 *  neither shape yields an id + slug + side. */
export function normalizePolyUsOpenOrder(raw: Record<string, unknown>): PolyUsRelayOpenOrder | null {
  const id = typeof raw.id === 'string' ? raw.id : typeof raw.orderId === 'string' ? raw.orderId : null;
  const marketSlug = typeof raw.marketSlug === 'string' ? raw.marketSlug : null;
  if (!id || !marketSlug) return null;
  let side: 'yes' | 'no' | null = raw.side === 'yes' || raw.side === 'no' ? raw.side : null;
  let action: 'buy' | 'sell' | null = raw.action === 'buy' || raw.action === 'sell' ? raw.action : null;
  if (!side || !action) {
    const sa = sideActionFromIntent(raw.intent);
    if (!sa) return null;
    side = sa.side;
    action = sa.action;
  }
  const longCents =
    num(raw.priceCentsLong) ??
    (() => {
      const price = raw.price as { value?: unknown } | undefined;
      const v = num(price?.value);
      return v == null ? null : Math.round(v * 10000) / 100;
    })();
  const sideCents = num(raw.priceCents) ?? (longCents == null ? null : side === 'yes' ? longCents : Math.round((100 - longCents) * 100) / 100);
  if (longCents == null || sideCents == null) return null;
  const quantity = num(raw.quantity) ?? 0;
  const filled = num(raw.quantityFilled) ?? num(raw.cumQuantity) ?? 0;
  const remaining = num(raw.remaining) ?? num(raw.leavesQuantity) ?? Math.max(0, quantity - filled);
  const meta = raw.marketMetadata as { title?: unknown; team?: { league?: unknown } } | undefined;
  return {
    id,
    marketSlug,
    side,
    action,
    priceCents: sideCents,
    priceCentsLong: longCents,
    quantity,
    quantityFilled: filled,
    remaining,
    tif: typeof raw.tif === 'string' ? (TIF_SHORT[raw.tif] ?? null) : null,
    goodTillTime: typeof raw.goodTillTime === 'string' ? raw.goodTillTime : null,
    postOnly: typeof raw.postOnly === 'boolean' ? raw.postOnly : typeof raw.participateDontInitiate === 'boolean' ? raw.participateDontInitiate : null,
    state: typeof raw.state === 'string' ? raw.state.replace(/^ORDER_STATE_/, '') : null,
    createdAt: typeof raw.createdAt === 'string' ? raw.createdAt : typeof raw.createTime === 'string' ? raw.createTime : null,
    marketLabel: typeof raw.marketLabel === 'string' ? raw.marketLabel : typeof meta?.title === 'string' ? meta.title : null,
    marketSport: typeof raw.marketSport === 'string' ? raw.marketSport : typeof meta?.team?.league === 'string' ? meta.team.league : null,
  };
}

export type ListPolyUsOpenOrdersResult =
  | { ok: true; orders: PolyUsRelayOpenOrder[] }
  | RelayFailure;

export async function listPolyUsOpenOrdersViaRelay(): Promise<ListPolyUsOpenOrdersResult> {
  const r = await readRelay('/orders');
  if (!r.ok) return r;
  if (!isRowObjectArray(r.body.orders)) return { ok: false, status: r.status, error: 'malformed orders payload' };
  const orders: PolyUsRelayOpenOrder[] = [];
  for (const row of r.body.orders) {
    const o = normalizePolyUsOpenOrder(row);
    if (o) orders.push(o);
  }
  return { ok: true, orders };
}

export type CancelPolyUsOrderResult = { ok: true } | RelayFailure;

export async function cancelPolyUsOrderViaRelay(orderId: string, marketSlug?: string): Promise<CancelPolyUsOrderResult> {
  const q = marketSlug ? `?marketSlug=${encodeURIComponent(marketSlug)}` : '';
  const r = await readRelay(`/orders/${encodeURIComponent(orderId)}${q}`, { method: 'DELETE' });
  return r.ok ? { ok: true } : r;
}

// ── Positions / P&L / realized (ledger-derived, R3) ───────────────────────

export interface PolyUsRelayPosition {
  /** The market slug (the ledger's ticker column for this venue). */
  ticker: string;
  marketSlug: string;
  side: 'yes' | 'no';
  netCount: number;
  avgEntryPriceCents: number;
  lastFillAt?: string | null;
  costBasisCents?: number;
  realizedPnlCents: number;
  /** Null = fee coverage unknown for the bucket, never $0. */
  feesPaidCents?: number | null;
  openFeesCents?: number | null;
  totalBoughtCount?: number;
  totalSoldCount?: number;
  totalBuyCostCents?: number;
  marketLabel: string | null;
  marketSport: string | null;
}

export interface PolyUsRelayPnl {
  realizedPnlCents: number;
  realizedFeesCents: number | null;
  realizedPnlNetCents: number | null;
  openCount: number;
  openNotionalCents: number;
}

export type ListPolyUsPositionsResult =
  | { ok: true; positions: PolyUsRelayPosition[]; credentialContext?: PolyUsReadCredentialContext }
  | RelayFailure;

export async function listPolyUsPositionsViaRelay(): Promise<ListPolyUsPositionsResult> {
  const r = await readRelay('/positions');
  if (!r.ok) return r;
  if (!isRowObjectArray(r.body.positions)) return { ok: false, status: r.status, error: 'malformed positions payload' };
  const positions = r.body.positions.map((p) => ({
    ...(p as unknown as PolyUsRelayPosition),
    marketSlug: typeof p.marketSlug === 'string' ? p.marketSlug : String(p.ticker ?? ''),
    marketLabel: typeof p.marketLabel === 'string' ? p.marketLabel : null,
    marketSport: typeof p.marketSport === 'string' ? p.marketSport : null,
  }));
  return {
    ok: true,
    positions,
    ...(r.body.credentialContext === undefined ? {} : { credentialContext: parsePolyUsReadCredentialContext(r.body.credentialContext) }),
  };
}

export type GetPolyUsPnlResult =
  | { ok: true; pnl: PolyUsRelayPnl; credentialContext?: PolyUsReadCredentialContext }
  | RelayFailure;

export async function getPolyUsPnlViaRelay(): Promise<GetPolyUsPnlResult> {
  const r = await readRelay('/pnl');
  if (!r.ok) return r;
  const b = r.body;
  const realized = num(b.realizedPnlCents);
  if (realized == null) return { ok: false, status: r.status, error: 'malformed pnl payload' };
  return {
    ok: true,
    pnl: {
      realizedPnlCents: realized,
      realizedFeesCents: num(b.realizedFeesCents),
      realizedPnlNetCents: num(b.realizedPnlNetCents),
      openCount: num(b.openCount) ?? 0,
      openNotionalCents: num(b.openNotionalCents) ?? 0,
    },
    ...(b.credentialContext === undefined ? {} : { credentialContext: parsePolyUsReadCredentialContext(b.credentialContext) }),
  };
}

export interface PolyUsRelayRealizedBucket {
  ticker: string;
  market_slug: string;
  side: 'yes' | 'no';
  market_label: string | null;
  market_sport: string | null;
  netCount: number;
  totalBoughtCount: number;
  totalBuyCostCents: number;
  totalSoldCount: number;
  totalSellRevCents: number;
  heldCostBasisCents: number;
  runningRealizedPnlGrossCents: number;
  buyFeesActualCents: number;
  sellFeesActualCents: number;
  hasAnyActualBuyFee: boolean;
  hasAnyActualSellFee: boolean;
  buyFillCount?: number;
  sellFillCount?: number;
  buyFeeKnownFillCount?: number;
  sellFeeKnownFillCount?: number;
  isStillOpen: boolean;
  settlement: 'yes' | 'no' | 'void' | null;
  settledAt: string | null;
  lastFillAt: string;
}

export type ListPolyUsRealizedResult =
  | { ok: true; buckets: PolyUsRelayRealizedBucket[]; credentialContext?: PolyUsReadCredentialContext }
  | RelayFailure;

export async function listPolyUsRealizedViaRelay(): Promise<ListPolyUsRealizedResult> {
  const r = await readRelay('/realized');
  if (!r.ok) return r;
  if (!isRowObjectArray(r.body.buckets)) return { ok: false, status: r.status, error: 'malformed realized payload' };
  return {
    ok: true,
    buckets: r.body.buckets as unknown as PolyUsRelayRealizedBucket[],
    ...(r.body.credentialContext === undefined ? {} : { credentialContext: parsePolyUsReadCredentialContext(r.body.credentialContext) }),
  };
}

// ── Fills ledger ──────────────────────────────────────────────────────────

export type ListPolyUsFillsResult =
  | { ok: true; fills: PolymarketUsFill[]; credentialContext?: PolyUsReadCredentialContext }
  | RelayFailure;

export async function listPolyUsFillsViaRelay(limit?: number): Promise<ListPolyUsFillsResult> {
  const r = await readRelay(`/fills${limit ? `?limit=${limit}` : ''}`);
  if (!r.ok) return r;
  if (!isRowObjectArray(r.body.fills)) return { ok: false, status: r.status, error: 'malformed fills payload' };
  return {
    ok: true,
    fills: r.body.fills as unknown as PolymarketUsFill[],
    ...(r.body.credentialContext === undefined ? {} : { credentialContext: parsePolyUsReadCredentialContext(r.body.credentialContext) }),
  };
}

// ── Balance ───────────────────────────────────────────────────────────────

export type GetPolyUsBalanceResult =
  | { ok: true; buyingPowerUsd: number | null; currentBalanceUsd: number | null }
  | RelayFailure;

export async function getPolyUsBalanceViaRelay(): Promise<GetPolyUsBalanceResult> {
  const r = await readRelay('/balances');
  if (!r.ok) return r;
  // The venue double-wraps: { balances: { balances: [...] } }; tolerate a flat list too.
  const outer = r.body.balances as { balances?: unknown } | unknown[] | undefined;
  const list: PolymarketUsBalance[] = Array.isArray(outer)
    ? (outer as PolymarketUsBalance[])
    : Array.isArray((outer as { balances?: unknown })?.balances)
      ? ((outer as { balances: PolymarketUsBalance[] }).balances)
      : [];
  const usd = list.find((b) => !b.currency || b.currency === 'USD') ?? list[0];
  return {
    ok: true,
    buyingPowerUsd: buyingPowerUsd(list),
    currentBalanceUsd: usd ? num(usd.currentBalance) : null,
  };
}
